import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BiSolidImageAdd, BiSolidVolumeFull, BiX } from "react-icons/bi";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import PopoverWithAudio, {
  IPopoverWithAudioType
} from "../../components/common/PopoverWithAudio";
import ProBadge from "../../components/ui/ProBadge";
import { AlertsType } from "../../constants/alerts.constant";
import usePermission from "../../hooks/usePermission";
import Utilities from "../../utils";
import {
  handleResetAppearance,
  handleUpdateAppearanceItem,
  selectAppearanceState
} from "./appearanceSlice";
import ColorDropDown from "./ColorDropDown";
import { IAiAppearance } from "./types";
import AppearanceEnableSeo from "./AppearanceEnableSeo";

const AppearanceHumanClone = () => {
  const { t, i18n } = useTranslation();

  const dispatch = useAppDispatch();

  const permissions = usePermission();
  const { configs } = useAppSelector(selectAppearanceState);

  const avatarRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    watch,
    setValue,
    formState: { errors },
    control
  } = useFormContext<IAiAppearance>();

  // ========================= Avatar ============================ //
  const [avatarsByUser, setAvatarsByUser] = useState<
    string | ArrayBuffer | null
  >();

  const handleSelectCustomAvatar = () => {
    if (avatarRef.current) {
      avatarRef.current.value = "";
      avatarRef.current.click();
    }
  };

  const handleVerifyAndSelectCustomAvatar = (e: any) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.name.length > 150) {
        toast.error(t?.(AlertsType.ISSTKO007, { chars: 150 }));
        return false;
      }

      if (file.size * 0.000001 > 10) {
        toast.error(t(AlertsType.ISSTKO008, { size: 10 }));
        return false;
      }

      if (
        !["image/jpeg", "image/jpg", "image/png", "image/gif"].includes(
          file.type
        )
      ) {
        toast.error(t(AlertsType.ISSTKO009));
        return false;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setAvatarsByUser(reader.result);
        handleUpdateItem("logo", file);
      };
      reader.onerror = (error) => console.log(error);
    }
  };

  const handleRemoveClientAvatar = () => {
    setAvatarsByUser(null);
    handleUpdateItem("logo", "");
  };

  // ========================= General ============================ //
  const handleUpdateItem = (key: keyof IAiAppearance, value: any) => {
    setValue(key, value);
    dispatch(handleUpdateAppearanceItem({ key, value }));
  };

  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(
    null
  );

  const handleVoiceResponseSound = () => {
    const language = i18n.language;
    const gender = watch("assistantGender");

    if (language && gender) {
      const url = Utilities.VoiceResponseSound.find(
        (dr) => dr.language === language && dr.gender === gender
      )?.cdnPath;
      if (url) {
        const audio = new Audio(url);

        if (currentAudio) {
          currentAudio.pause();
          currentAudio.currentTime = 0;
        }

        setCurrentAudio(audio);
        audio.play();
      }
    }
  };

  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
    };
  }, [currentAudio]);

  // Popover voice and texts
  const voiceCommandPopData: IPopoverWithAudioType = {
    audioUrl: Utilities.VoiceCommandAudioLinks[i18n.language],
    contentText: t("DashboardSettingCustomizeChat/pop-07")
  };

  const voiceResponsePopData: IPopoverWithAudioType = {
    audioUrl: Utilities.VoiceResponseAudioLinks[i18n.language],
    contentText: t("DashboardSettingCustomizeChat/pop-14")
  };

  useEffect(() => {
    if (
      configs?.logo &&
      configs.logo !==
        "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png" &&
      configs.logo !==
        "https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png" &&
      typeof configs.logo !== "object"
    ) {
      setAvatarsByUser(configs.logo);
      handleUpdateItem("logo", configs?.logo);
    }
  }, [configs?.logo]);

  return (
    <div className="appearance-human-clone-scenario tw-w-full">
      {/* Avatar */}
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
        {/* label */}
        <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
          {t("DashboardSettingCustomizeChat/t-04")}
          {!permissions.settingsPage.widget.customization.avatar && (
            <ProBadge className="tw-ms-2" />
          )}
        </div>
        {/* content */}
        <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-6 tw-flex tw-w-full tw-flex-wrap">
          <button
            type="button"
            disabled={!permissions.settingsPage.widget.customization.avatar}
            onClick={() =>
              handleUpdateItem(
                "logo",
                "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png"
              )
            }
            className={`tw-p-0 tw-bg-transparent tw-w-10 tw-h-10 tw-cursor-pointer tw-rounded-full tw-border-4 ${
              (configs as IAiAppearance).logo ===
              "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png"
                ? "tw-border-blue-600"
                : "tw-border-gray-200"
            } tw-border-solid`}
          >
            <img
              className="tw-w-full tw-h-full tw-rounded-full tw-object-cover"
              src="https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png"
              alt=""
            />
          </button>

          <button
            type="button"
            disabled={!permissions.settingsPage.widget.customization.avatar}
            onClick={() =>
              handleUpdateItem(
                "logo",
                "https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png"
              )
            }
            className={`tw-p-0 tw-bg-transparent tw-w-10 tw-h-10 tw-cursor-pointer tw-rounded-full tw-border-4 ${
              (configs as IAiAppearance).logo ===
              "https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png"
                ? "tw-border-blue-600"
                : "tw-border-gray-200"
            } tw-border-solid`}
          >
            <img
              className="tw-w-full tw-h-full tw-rounded-full tw-object-cover"
              src="https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png"
              alt=""
            />
          </button>

          {avatarsByUser && (
            <div
              className={`tw-w-10 tw-h-10 tw-cursor-pointer tw-rounded-full tw-border-4 tw-relative ${
                typeof (configs as IAiAppearance).logo === "object" ||
                ((configs as IAiAppearance).logo !==
                  "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png" &&
                  (configs as IAiAppearance).logo !==
                    "https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png")
                  ? "tw-border-blue-600"
                  : "tw-border-gray-200"
              } tw-border-solid`}
            >
              <BiX
                size={22}
                className="tw-text-gray-100 tw-absolute tw-right-[-5px] tw-top-[-10px] tw-cursor-pointer tw-bg-black tw-rounded-full tw-z-10"
                onClick={handleRemoveClientAvatar}
              />
              <button
                type="button"
                className="tw-p-0 tw-bg-transparent tw-border-0 tw-rounded-full tw-w-full tw-relative"
                disabled={!permissions.settingsPage.widget.customization.avatar}
                onClick={() => {
                  if (
                    avatarRef.current &&
                    avatarRef.current.files &&
                    avatarRef.current.files.length > 0
                  )
                    handleUpdateItem("logo", avatarRef.current?.files[0]);
                  else handleUpdateItem("logo", avatarsByUser);
                }}
              >
                <img
                  className="tw-absolute tw-w-full tw-h-9 tw-top-[-20px] tw-left-0 tw-right-0 tw-bottom-0 tw-rounded-full tw-object-cover"
                  src={avatarsByUser.toString()}
                  alt=""
                />
              </button>
            </div>
          )}

          {/* Image uploader */}
          <button
            type="button"
            className="tw-pl-3 tw-pr-4 tw-py-2 tw-bg-white tw-rounded-[99px] tw-border tw-border-solid tw-border-gray-300 tw-justify-start tw-items-center tw-gap-2 tw-flex tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal"
            onClick={handleSelectCustomAvatar}
            disabled={!permissions.settingsPage.widget.customization.avatar}
          >
            <BiSolidImageAdd size={22} className="tw-text-gray-400 tw-mr-2" />
            {t("DashboardSettingCustomizeChat/pop-05")}
          </button>

          <input
            type="file"
            hidden
            ref={avatarRef}
            accept="image/png, image/jpeg, image/jpg, image/gif"
            onChange={handleVerifyAndSelectCustomAvatar}
          />
        </div>
        {/* Validation */}
        {errors.logo && (
          <span className="tw-text-red-500 tw-text-sm tw-block">
            {errors.logo?.message}
          </span>
        )}
      </div>
      <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

      {/* Theme */}
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
        <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
          {t("DashboardSettingCustomizeChat/l-06")}
        </div>
        <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-6 tw-flex tw-w-full">
          <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-w-full">
            <div className="tw-w-full tw-col-span-12">
              <Form.Group>
                <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t("DashboardSettingCustomizeChat/l-12")}
                  {!permissions.settingsPage.widget.customization.theme && (
                    <ProBadge className="tw-ms-2" />
                  )}
                </Form.Label>
                <Form.Select
                  disabled={
                    !permissions.settingsPage.widget.customization.theme
                  }
                  isInvalid={!!errors.theme}
                  {...register("theme", {
                    onChange(event) {
                      dispatch(
                        handleUpdateAppearanceItem({
                          key: "theme",
                          value: event.target.value
                        })
                      );
                    }
                  })}
                >
                  <option value={"light"}>
                    {t("DashboardSettingCustomizeChat/l-05")}
                  </option>
                  <option value={"dark"}>
                    {t("DashboardSettingCustomizeChat/l-07")}
                  </option>
                </Form.Select>
                {errors.theme && (
                  <span className="tw-text-red-500 tw-text-sm tw-block">
                    {errors.theme?.message}
                  </span>
                )}
              </Form.Group>
            </div>
            <div className="tw-w-full tw-col-span-12">
              <Form.Group>
                <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t("DashboardSettingCustomizeChat/t-01")}
                  {!permissions.settingsPage.widget.customization.colors && (
                    <ProBadge className="tw-ms-2" />
                  )}
                </Form.Label>
                <Controller
                  control={control}
                  name="color"
                  render={({ field: { onChange, value } }) => (
                    <ColorDropDown
                      acceptableColors={[
                        { label: "Grey", value: "#6B7280" },
                        { label: "Red", value: "#EF4444" },
                        { label: "Orange", value: "#F97316" },
                        { label: "Yellow", value: "#F59E0B" },
                        { label: "Green", value: "#10B981" },
                        { label: "Teal", value: "#14B8A6" },
                        { label: "Cyan", value: "#06B6D4" },
                        { label: "Light Blue", value: "#0EA5E9" },
                        { label: "Blue", value: "#3B82F6" },
                        { label: "Indigo", value: "#6366F1" },
                        { label: "Purple (Violet)", value: "#8B5CF6" },
                        { label: "Pink", value: "#EC4899" }
                      ]}
                      disabled={
                        !permissions.settingsPage.widget.customization.colors
                      }
                      isInvalid={!!errors.color}
                      errorMessage={errors.color?.message}
                      onColorChanged={(color) => {
                        onChange(color);

                        dispatch(
                          handleUpdateAppearanceItem({
                            key: "color",
                            value: color
                          })
                        );
                      }}
                      currentColor={value}
                    />
                  )}
                />
              </Form.Group>
            </div>
          </div>
        </div>
      </div>
      <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />

      {/* Voice Messages */}
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
        <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
          {t("DashboardSettingCustomizeChat/l-16")}
        </div>
        <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
          <Form.Group className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
            <Form.Check
              disabled={
                !permissions.settingsPage.widget.customization.voiceCommand
              }
              type="switch"
              id="isVoiceActive"
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t("DashboardSettingCustomizeChat/allow-send-voice")}
              isInvalid={!!errors.isVoiceActive}
              feedbackType="invalid"
              feedback={errors.isVoiceActive?.message}
              {...register("isVoiceActive", {
                onChange: (e) => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: "isVoiceActive",
                      value: e.target.checked
                    })
                  );
                }
              })}
            />
            {!permissions.settingsPage.widget.customization.voiceCommand && (
              <ProBadge className="tw-ms-2" />
            )}
            <PopoverWithAudio
              data={{
                contentText: voiceCommandPopData.contentText,
                audioUrl: voiceCommandPopData.audioUrl
              }}
            />
          </Form.Group>

          <Form.Group className="tw-w-full tw-flex tw-items-center tw-gap-2">
            <Form.Check
              disabled={
                !permissions.settingsPage.widget.customization.voiceResponse
              }
              type="switch"
              id="voiceResponse"
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t("DashboardSettingCustomizeChat/allow-receive-voice")}
              isInvalid={!!errors.voiceResponse}
              feedbackType="invalid"
              feedback={errors.voiceResponse?.message}
              {...register("voiceResponse", {
                onChange: (e) => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: "voiceResponse",
                      value: e.target.checked
                    })
                  );
                }
              })}
            />
            {!permissions.settingsPage.widget.customization.voiceResponse && (
              <ProBadge
                className="tw-ms-2"
                onClose={() => {
                  dispatch(handleResetAppearance());
                }}
              />
            )}

            <PopoverWithAudio
              data={{
                contentText: voiceResponsePopData.contentText,
                audioUrl: voiceResponsePopData.audioUrl
              }}
            />
          </Form.Group>

          <Form.Group
            className={`tw-w-full tw-flex tw-items-center tw-gap-5 ${
              watch("voiceResponse") == true ? "tw-block" : "tw-hidden"
            }`}
          >
            <Form.Select
              className="md:tw-w-1/2 tw-w-full"
              {...register("assistantGender", {
                onChange: (e) => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: "assistantGender",
                      value: e.target.value
                    })
                  );
                }
              })}
            >
              <option value="female">
                Louise ({t("DashboardSettingCustomizeChat/female")})
              </option>
              <option value="male">
                Jonathan ({t("DashboardSettingCustomizeChat/male")})
              </option>
            </Form.Select>
            <div
              className="tw-cursor-pointer"
              onClick={handleVoiceResponseSound}
              onKeyDown={handleVoiceResponseSound}
              role="button"
              tabIndex={0}
            >
              <BiSolidVolumeFull className="tw-w-6 tw-h-6 tw-text-blue-500 " />
            </div>
          </Form.Group>

          <AppearanceEnableSeo
            control={control}
            errors={errors}
            register={register}
            watch={watch}
          />
        </div>
      </div>

      <hr className="tw-border-gray-200 tw-pb-6 ty-m-0" />
    </div>
  );
};

export default AppearanceHumanClone;
